// Environment configuration for frontend-ts
// This file ensures consistent API URLs across all environments

interface EnvironmentConfig {
  API_BASE_URL: string;
  WS_URL: string;
  DEBUG_MODE: boolean;
  LOG_LEVEL: string;
  CACHE_DURATION: number;
  AUTO_REFRESH_INTERVAL: number;
  ENABLE_ANALYTICS: boolean;
  ENABLE_CRASH_REPORTING: boolean;
  ENABLE_PERFORMANCE_MONITORING: boolean;
  ENABLE_REAL_TIME_UPDATES: boolean;
  ENABLE_OFFLINE_MODE: boolean;
  APP_NAME: string;
  APP_VERSION: string;
  APP_ENVIRONMENT: string;
}

const API_V1_SUFFIX = '/api/v1';

const normalizeApiBaseUrl = (value: string): string => {
  const trimmed = value.trim().replace(/\/+$/, '');
  return trimmed.endsWith(API_V1_SUFFIX) ? trimmed : `${trimmed}${API_V1_SUFFIX}`;
};

const ENV_API = normalizeApiBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1');
const ENV_WS = process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws';

// Base production defaults – used as fallback when env vars are absent
const PRODUCTION_DEFAULTS = {
  API_BASE_URL: 'https://hub.auratechnologies.co.zw/backend/api/v1',
  WS_URL: 'wss://hub.auratechnologies.co.zw/backend/ws',
};

// Production configuration
const PRODUCTION_CONFIG: EnvironmentConfig = {
  // Prefer the env var so a redeploy with a new URL just works
  API_BASE_URL: ENV_API !== 'http://localhost:8000/api/v1' ? ENV_API : PRODUCTION_DEFAULTS.API_BASE_URL,
  WS_URL: process.env.REACT_APP_WS_URL || PRODUCTION_DEFAULTS.WS_URL,
  DEBUG_MODE: false,
  LOG_LEVEL: 'error',
  CACHE_DURATION: 600000,
  AUTO_REFRESH_INTERVAL: 120000,
  ENABLE_ANALYTICS: true,
  ENABLE_CRASH_REPORTING: true,
  ENABLE_PERFORMANCE_MONITORING: true,
  ENABLE_REAL_TIME_UPDATES: false,
  ENABLE_OFFLINE_MODE: true,
  APP_NAME: 'AuraHub',
  APP_VERSION: '1.0.0',
  APP_ENVIRONMENT: 'production'
};

// Development configuration
const DEVELOPMENT_CONFIG: EnvironmentConfig = {
  API_BASE_URL: ENV_API,
  WS_URL: ENV_WS,
  DEBUG_MODE: true,
  LOG_LEVEL: 'debug',
  CACHE_DURATION: 300000,
  AUTO_REFRESH_INTERVAL: 30000,
  ENABLE_ANALYTICS: false,
  ENABLE_CRASH_REPORTING: false,
  ENABLE_PERFORMANCE_MONITORING: false,
  ENABLE_REAL_TIME_UPDATES: true,
  ENABLE_OFFLINE_MODE: false,
  APP_NAME: 'AuraHub (Dev)',
  APP_VERSION: '1.0.0',
  APP_ENVIRONMENT: 'development'
};

// Determine which configuration to use
const getEnvironmentConfig = (): EnvironmentConfig => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Development builds always use the dev config (reads from .env)
  if (isDevelopment) {
    return DEVELOPMENT_CONFIG;
  }

  // Production / staging builds – REACT_APP_API_URL always wins
  return PRODUCTION_CONFIG;
};

// Build config
const _rawConfig = getEnvironmentConfig();

export const ENV_CONFIG = _rawConfig;
export const PUBLIC_BASE_URL = _rawConfig.API_BASE_URL.replace(/\/api\/v1\/?$/, '');

// Export individual values for convenience
export const {
  API_BASE_URL,
  WS_URL,
  DEBUG_MODE,
  LOG_LEVEL,
  CACHE_DURATION,
  AUTO_REFRESH_INTERVAL,
  ENABLE_ANALYTICS,
  ENABLE_CRASH_REPORTING,
  ENABLE_PERFORMANCE_MONITORING,
  ENABLE_REAL_TIME_UPDATES,
  ENABLE_OFFLINE_MODE,
  APP_NAME,
  APP_VERSION,
  APP_ENVIRONMENT
} = ENV_CONFIG;
